import { css } from '@emotion/react';
import { theme } from '@frontend/theme';

export const GRID_AREAS = {
  CONTENT: 'content',
  META: 'meta',
  AVATAR: 'avatar',
  EMPTY: 'empty',
} as const;

const containerStyles = ({ direction }) => {
  const gridRowTemplate1 =
    direction === 'outbound'
      ? `${GRID_AREAS.CONTENT} ${GRID_AREAS.AVATAR}`
      : `${GRID_AREAS.AVATAR} ${GRID_AREAS.CONTENT}`;
  const gridRowTemplate2 =
    direction === 'outbound' ? `${GRID_AREAS.META} ${GRID_AREAS.EMPTY}` : `${GRID_AREAS.EMPTY} ${GRID_AREAS.META}`;

  return css`
    width: 100%;
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: ${direction === 'inbound' ? 'auto 1fr' : '1fr auto'};
    padding: 0;
    gap: ${theme.spacing(0.5, 1)};
    grid-template-areas:
      '${gridRowTemplate1}'
      '${gridRowTemplate2}';
    align-items: end;
    margin-top: ${theme.spacing(1)};
  `;
};

const contentStyle = ({ direction }) => {
  return css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: ${direction === 'inbound' ? 'start' : 'end'};
    gap: ${theme.spacing(1)};
    position: relative;
    grid-area: ${GRID_AREAS.CONTENT};

    .bubbles-container {
      display: flex;
      flex-direction: column;
      max-width: min(95%, 600px);
      gap: ${theme.spacing(1)};
      align-items: ${direction === 'inbound' ? 'start' : 'end'};
    }

    .image-loader {
      padding: ${theme.spacing(6)};
      border: 1px solid ${theme.colors.neutral20};
      display: flex;
      border-radius: 8px;
      justify-content: center;
      align-items: center;
    }
  `;
};

const metaInfoStyle = ({ direction }) => css`
  grid-area: ${GRID_AREAS.META};
  display: flex;
  flex-direction: column;
  align-items: ${direction === 'inbound' ? 'flex-start' : 'flex-end'};

  .tags-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding-bottom: ${theme.spacing(0.5)};
    justify-content: ${direction === 'inbound' ? 'flex-start' : 'flex-end'};
    align-items: ${direction === 'inbound' ? 'flex-start' : 'flex-end'};
    gap: ${theme.spacing(1)};
  }

  .meta-info-container {
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: ${direction === 'outbound' ? 'flex-end' : 'flex-start'};
    flex-direction: ${direction === 'inbound' ? 'row-reverse' : 'row'};
    padding-bottom: ${theme.spacing(0.5)};
  }

  .metadata-container {
    display: flex;
    gap: ${theme.spacing(1)};
  }
`;

const actionsStyle = css`
  border: 1px solid ${theme.colors.neutral20};
  border-radius: ${theme.borderRadius.small};
  box-shadow: ${theme.shadows.light};
  position: absolute;
  top: ${theme.spacing(-2)};
  background-color: ${theme.colors.white};
  display: flex;
  z-index: ${theme.zIndex.popover};
  padding: ${theme.spacing(0.25)};

  & button {
    padding: ${theme.spacing(0.5)};
    height: unset;
  }
`;

const bubbleStyles = ({ direction, isImage, backgroundColor, maxWidth, maxHeight }) => css`
  list-style-type: none;
  padding: ${theme.spacing(isImage ? 0 : 2)};
  display: ${isImage ? 'list-item' : 'flex'};
  overflow: ${isImage ? 'hidden' : 'visible'};
  gap: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.medium};
  background-color: ${backgroundColor ?? (direction === 'inbound' ? theme.colors.neutral5 : theme.colors.primary5)};
  white-space: pre-line;
  width: fit-content;
  max-height: ${maxHeight ?? (isImage ? 'min(40vh, 480px)' : '100%')};
  max-width: ${maxWidth ?? (isImage ? 'min(40%, 320px)' : '100%')};

  .img-bubble {
    width: 100%;
    border-radius: ${theme.borderRadius.medium};
  }
`;

const previewModalStyles = css`
  padding: 0;
  max-width: unset;
  overflow: visible;

  .wv-modal-flex-content {
    padding: 0;
    max-height: 78vh;
  }

  img {
    max-height: inherit;
  }

  .btn-containers {
    position: absolute;
    display: flex;
    gap: ${theme.spacing(1)};
    top: ${theme.spacing(-4)};
    right: 0;
  }

  .download-button {
    background-color: ${theme.colors.white};
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    padding: 0;
  }

  .close-button {
    background-color: ${theme.colors.white};
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    padding: 0;
  }
`;

export const easeInOutVariants = {
  visible: { opacity: 1, transition: { duration: 0.3 } },
  hidden: { opacity: 0, transition: { duration: 0.3 } },
};

export const slideInVariants = {
  hidden: { height: 0, opacity: 0 },
  visible: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

export const ChatStyles = {
  container: containerStyles,
  content: contentStyle,
  meta: metaInfoStyle,
  actions: actionsStyle,
  bubble: bubbleStyles,
  previewModal: previewModalStyles,
};
